// src/components/captures/TaskCandidateItem.tsx
import React, { useState, useEffect, useRef } from 'react';
import { TaskCategory, TaskPriority, TaskEnergyLevel } from '../../types/task';
import { Check, ChevronUp, ChevronDown, Trash2 } from '../common/icons';
import { formatDateToString } from '../../utils/dateUtils';
import { logError } from '../../utils/logger';

interface TaskCandidate {
  id: string;
  title: string;
  notes?: string;
  category?: TaskCategory;
  priority?: TaskPriority;
  energyLevel?: TaskEnergyLevel;
  estimatedTime?: number;
  confidence: number;
  targetDate?: string;
  isSelected: boolean;
}

interface TaskCandidateItemProps {
  task: TaskCandidate;
  onToggleSelection: () => void;
  onSaveEdit: (updates: Partial<TaskCandidate>) => void;
  onDelete?: () => void;
  showDeleteButton?: boolean;
  autoExpand?: boolean; // 新任務自動展開
}

const TaskCandidateItem: React.FC<TaskCandidateItemProps> = ({
  task,
  onToggleSelection,
  onSaveEdit,
  onDelete,
  showDeleteButton = false,
  autoExpand = false
}) => {
  // 展開/收合狀態
  const [isExpanded, setIsExpanded] = useState(autoExpand);
  const titleInputRef = useRef<HTMLInputElement>(null);

  // 低信心度閾值（75%）
  const isLowConfidence = task.confidence < 0.75;

  // 自動展開時聚焦標題輸入框
  useEffect(() => {
    if (autoExpand && isExpanded && titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, [autoExpand, isExpanded]);

  // 判斷日期屬於哪個預設選項
  const getOptionFromDate = (dateString: string): string => {
    if (!dateString) return '';

    try {
      let targetDate: Date;
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateString.split('-').map(Number);
        targetDate = new Date(year, month - 1, day);
      } else {
        targetDate = new Date(dateString);
      }

      if (isNaN(targetDate.getTime())) {
        return dateString;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      targetDate.setHours(0, 0, 0, 0);

      if (targetDate.getTime() === today.getTime()) {
        return 'today';
      } else if (targetDate.getTime() === tomorrow.getTime()) {
        return 'tomorrow';
      } else {
        const dayOfWeek = today.getDay();
        const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
        const thisSunday = new Date(today);
        thisSunday.setDate(today.getDate() + daysUntilSunday);

        if (targetDate.getTime() === thisSunday.getTime()) {
          return 'this-week';
        }

        const nextSunday = new Date(thisSunday);
        nextSunday.setDate(thisSunday.getDate() + 7);

        if (targetDate.getTime() === nextSunday.getTime()) {
          return 'next-week';
        }
      }

      return dateString;
    } catch (error) {
      logError('解析日期錯誤', error);
      return dateString;
    }
  };

  // 將日期選項轉換為實際日期
  const getDateFromOption = (option: string): Date | null => {
    const now = new Date();

    switch(option) {
      case 'today':
        return now;

      case 'tomorrow':
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow;

      case 'this-week':
        const thisWeekEnd = new Date(now);
        const dayOfWeek = now.getDay();

        if (dayOfWeek === 0) {
          return thisWeekEnd;
        } else {
          const daysUntilSunday = 7 - dayOfWeek;
          thisWeekEnd.setDate(now.getDate() + daysUntilSunday);
          return thisWeekEnd;
        }

      case 'next-week':
        const nextWeekEnd = new Date(now);
        const currentDayOfWeek = now.getDay();
        const daysUntilNextSunday = (7 - currentDayOfWeek) + 7;
        nextWeekEnd.setDate(now.getDate() + daysUntilNextSunday);
        return nextWeekEnd;

      default:
        return null;
    }
  };

  // Inline edit: 即時保存變更
  const handleFieldChange = (field: keyof TaskCandidate, value: unknown) => {
    let resolvedValue: unknown = value;
    // 特殊處理日期選項
    if (field === 'targetDate' && value) {
      const strValue = typeof value === 'string' ? value : String(value);
      const convertedDate = getDateFromOption(strValue);
      if (convertedDate) {
        resolvedValue = formatDateToString(convertedDate);
      } else if (strValue !== '') {
        try {
          const date = new Date(strValue);
          if (!isNaN(date.getTime())) {
            resolvedValue = formatDateToString(date);
          }
        } catch {
          // 保持原值
        }
      }
    }

    onSaveEdit({ [field]: resolvedValue });
  };

  // 格式化日期顯示
  const getFormattedDateDisplay = (targetDateValue: string): string => {
    if (!targetDateValue) return '';

    if (['today', 'tomorrow', 'this-week', 'next-week'].includes(targetDateValue)) {
      const date = getDateFromOption(targetDateValue);
      if (date) {
        return formatDateToString(date);
      }
    }

    try {
      const date = new Date(targetDateValue);
      if (!isNaN(date.getTime())) {
        return formatDateToString(date);
      }
    } catch (error) {
      logError('格式化日期錯誤', error);
    }

    return targetDateValue;
  };

  // 獲取當前日期對應的選項值
  const getCurrentDateOption = (): string => {
    if (!task.targetDate) return '';
    return getOptionFromDate(task.targetDate);
  };

  // 切換選中狀態（只在非展開時觸發）
  const handleCardClick = () => {
    if (!isExpanded) {
      onToggleSelection();
    }
  };

  // 展開編輯
  const handleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(true);
  };

  // 收合編輯
  const handleCollapse = () => {
    setIsExpanded(false);
  };

  return (
    <div
      className={`relative border rounded-lg transition-all duration-200 ${
        isExpanded
          ? 'border-sky-300 bg-white shadow-sm'
          : task.isSelected
          ? 'border-sky-300 bg-sky-50/50 shadow-sm cursor-pointer hover:shadow-md'
          : 'border-gray-200 bg-white hover:border-gray-300 cursor-pointer hover:shadow-sm'
      }`}
      onClick={handleCardClick}
    >
      <div className="p-3 sm:p-4" onClick={(e) => isExpanded && e.stopPropagation()}>
        <div className="flex items-start gap-3">
          {/* Checkbox */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelection();
            }}
            className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200 flex-shrink-0 ${
              task.isSelected
                ? 'bg-sky-500 border-sky-500 text-white'
                : 'border-gray-300 hover:border-sky-300'
            }`}
          >
            {task.isSelected && <Check size={14} strokeWidth={3} />}
          </button>

          {/* Task Content */}
          <div className="flex-1 min-w-0">
            {/* 標題列 - 收合時顯示標題文字，展開時顯示輸入框 */}
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2 flex-1">
                {isExpanded ? (
                  <input
                    ref={titleInputRef}
                    type="text"
                    value={task.title}
                    onChange={(e) => handleFieldChange('title', e.target.value)}
                    className="flex-1 px-2 py-1.5 -ml-2 border border-gray-200 rounded-md text-base font-medium min-h-[44px] focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                    placeholder="Enter task title..."
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <>
                    <h4
                      className="font-medium text-gray-900 text-base leading-snug cursor-pointer hover:text-sky-600"
                      onClick={handleExpand}
                    >
                      {task.title || <span className="text-gray-400 italic">Untitled task</span>}
                    </h4>
                    {isLowConfidence && (
                      <span className="text-amber-500 text-xs" title={`Low confidence: ${Math.round(task.confidence * 100)}%`}>
                        ⚠️
                      </span>
                    )}
                  </>
                )}
              </div>

              {/* 操作按鈕 */}
              <div className="flex items-center gap-1 ml-2">
                {isExpanded ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCollapse();
                    }}
                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all flex-shrink-0"
                    title="Collapse"
                  >
                    <ChevronUp size={14} />
                  </button>
                ) : (
                  <button
                    onClick={handleExpand}
                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all flex-shrink-0"
                    title="Edit task"
                  >
                    <ChevronDown size={14} />
                  </button>
                )}
                {showDeleteButton && onDelete && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete();
                    }}
                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all flex-shrink-0"
                    title="Delete task"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* 展開時顯示編輯表單 */}
            {isExpanded ? (
              <div className="space-y-3 mt-3">
                {/* 屬性網格 */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Duration</label>
                    <select
                      value={task.estimatedTime || 30}
                      onChange={(e) => handleFieldChange('estimatedTime', Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-md text-base min-h-[44px] focus:ring-2 focus:ring-sky-500 focus:border-transparent bg-white"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <option value={15}>15 min</option>
                      <option value={30}>30 min</option>
                      <option value={45}>45 min</option>
                      <option value={60}>1 hour</option>
                      <option value={90}>1.5 hours</option>
                      <option value={120}>2 hours</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Category</label>
                    <select
                      value={task.category || 'personal'}
                      onChange={(e) => handleFieldChange('category', e.target.value as TaskCategory)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-md text-base min-h-[44px] focus:ring-2 focus:ring-sky-500 focus:border-transparent bg-white"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <option value="work">Work</option>
                      <option value="personal">Personal</option>
                      <option value="health">Health</option>
                      <option value="social">Social</option>
                      <option value="family">Family</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Priority</label>
                    <select
                      value={task.priority || '2'}
                      onChange={(e) => handleFieldChange('priority', e.target.value as TaskPriority)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-md text-base min-h-[44px] focus:ring-2 focus:ring-sky-500 focus:border-transparent bg-white"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <option value="3">Low</option>
                      <option value="2">Medium</option>
                      <option value="1">High</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Target Date</label>
                    <select
                      value={getCurrentDateOption()}
                      onChange={(e) => handleFieldChange('targetDate', e.target.value || undefined)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-md text-base min-h-[44px] focus:ring-2 focus:ring-sky-500 focus:border-transparent bg-white"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <option value="">No date</option>
                      <option value="today">Today</option>
                      <option value="tomorrow">Tomorrow</option>
                      <option value="this-week">This Week</option>
                      <option value="next-week">Next Week</option>
                    </select>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Notes</label>
                  <textarea
                    value={task.notes || ''}
                    onChange={(e) => handleFieldChange('notes', e.target.value || undefined)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-200 rounded-md text-base leading-relaxed focus:ring-2 focus:ring-sky-500 focus:border-transparent resize-none"
                    placeholder="Add notes..."
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            ) : (
              /* 收合時顯示屬性標籤 */
              <div className="flex flex-wrap items-center gap-2 text-xs">
                {task.estimatedTime && (
                  <span className="text-gray-500">{task.estimatedTime} min</span>
                )}
                {task.category && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    task.category === 'work' ? 'bg-sky-100 text-sky-700' :
                    task.category === 'personal' ? 'bg-amber-100 text-amber-700' :
                    task.category === 'health' ? 'bg-green-100 text-green-700' :
                    task.category === 'family' ? 'bg-green-100 text-green-700' :
                    task.category === 'social' ? 'bg-pink-100 text-pink-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {task.category.charAt(0).toUpperCase() + task.category.slice(1)}
                  </span>
                )}
                {task.priority && task.priority !== '2' && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    task.priority === '1' ? 'bg-red-100 text-red-700' :
                    'bg-sky-100 text-sky-700'
                  }`}>
                    {task.priority === '1' ? 'High' : 'Low'}
                  </span>
                )}
                {task.targetDate && (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                    {getFormattedDateDisplay(task.targetDate)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskCandidateItem;
